/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ComponentPropsWithoutRef,
} from 'react';
import styled, { css } from 'styled-components';
import { isEmpty, isNil, debounce } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { useTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import type { SelectProps } from '@mui/material/Select';
import {
  Formik,
  Form as DefForm,
  type FormikConfig,
  type FormikProps,
  type FormikHelpers,
} from 'formik';
import type { TFunction } from 'i18next';
import * as Yup from 'yup';
import { useApolloClient } from '@apollo/client';
import {
  InputAdornment,
  MenuItem as DefMenuItem,
  Button as DefMuiButton,
} from '@mui/material';
import { getPrice } from '../../utils/currency';
import { sendSentryError } from '../../utils/sentry';
import {
  PortfolioPartitionAction,
  CurrencyPercentageCostDocument,
  type CurrencyInput,
  type CurrencyPercentageCostQuery,
  type CurrencyPercentageCostQueryVariables,
  type CurrencyPercentageCostQueryResult,
} from '../../apollo';
import { getUpMedia, getColor } from '../../styles';
import { useExchange, useAlert } from '../../providers';
import { Spinner } from '../../styled';
import { Button as DefButton } from '../buttons';
import { Checkbox, Input } from '../form-elements';
import {
  FormikInput as DefFormikInput,
  type FormikInputProps,
} from '../formik-elements';
import { Text } from '../texts';

export type PortfolioManipulationFormProps = {
  portfolioManipulationAction: PortfolioPartitionAction;
  disabled?: boolean;
  multiple?: boolean;
  withSelectAll?: boolean;
  options: Array<PortfolioManipulationFormCurrenciesOption>;
  inputsPrefix: string;
  buttonText: string;
  inputsAlwaysOneRowOnDesktop?: boolean;
  onSubmit: FormikConfig<FormValues>['onSubmit'];
} & Omit<
  ComponentPropsWithoutRef<typeof DefForm>,
  'onSubmit' | 'children' | 'ref'
>;

const PortfolioManipulationForm = ({
  disabled: disabledProp,
  portfolioManipulationAction,
  multiple = false,
  withSelectAll = false,
  options,
  inputsPrefix,
  buttonText,
  inputsAlwaysOneRowOnDesktop = false,
  onSubmit,
  ...props
}: PortfolioManipulationFormProps) => {
  const { t } = useTranslation();

  const formikRef = useRef<FormikProps<FormFieldValues> | null>(null);
  const redLabelStartTrialAlertShownRef = useRef(false);

  const [currencies, setCurrencies] = useState<SelectedCurrency[]>([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [percent, setPercent] = useState('');

  const { setAlert } = useAlert();
  const client = useApolloClient();
  const { exchange } = useExchange();
  const theme = useTheme();

  const allOptionIds = useMemo(
    () =>
      options
        .map(({ value }) => value)
        .filter((value) => value !== SELECT_ALL_VALUE),
    [options]
  );

  const {
    loading: loadingResultCost,
    error: errorOnResultCostLoad,
    value: resultCostResponse,
  } = useAsync(async () => {
    const singleCurrencyWithPercent = currencies.length === 1 && percent;
    const severalCurrencies = currencies.length > 1;

    if (!(singleCurrencyWithPercent || severalCurrencies)) {
      return Promise.resolve(null);
    }

    if (!exchange?.exchangeID) {
      throw new Error(
        'Cannot calculate percentage cost: exchange.exchangeID is missing'
      );
    }

    const currenciesVar: CurrencyInput[] = singleCurrencyWithPercent
      ? [
          {
            id: Number(currencies[0].id),
            percentage: Number(percent),
          },
        ]
      : currencies.map(({ id }) => ({
          id: Number(id),
        }));

    return client.query<
      CurrencyPercentageCostQuery,
      CurrencyPercentageCostQueryVariables
    >({
      query: CurrencyPercentageCostDocument,
      fetchPolicy: 'no-cache',
      variables: {
        partitionAction: portfolioManipulationAction,
        exchangeId: Number(exchange.exchangeID),
        currencies: currenciesVar,
      },
    });
  }, [
    exchange?.exchangeID,
    portfolioManipulationAction,
    client,
    currencies,
    percent,
  ]);

  const schema = useMemo(() => getSchema(t, multiple), [t, multiple]);

  const debouncedSetPercentage = useMemo(
    () =>
      debounce((value: InputValue) => {
        const percentValue = getInputValue(value);
        setPercent(typeof percentValue === 'string' ? percentValue : '');
      }, 500),
    []
  );

  const setSelectedCurrencies = useCallback(
    (selectedCurrencies: SelectedCurrency[]) => {
      setCurrencies((currentCurrencies) =>
        haveSameCurrencies(currentCurrencies, selectedCurrencies)
          ? currentCurrencies
          : selectedCurrencies
      );
    },
    []
  );

  const handleSelectClose = useCallback(() => {
    setSelectOpen(false);
  }, []);

  const forcePercentTo100 = useCallback(() => {
    debouncedSetPercentage.cancel();

    if (formikRef.current?.values.percent !== '100') {
      formikRef.current?.setFieldValue('percent', '100');
    }

    setPercent((currentPercent) =>
      currentPercent === '100' ? currentPercent : '100'
    );
  }, [debouncedSetPercentage]);

  const resultCostToRender = useMemo(() => {
    if (errorOnResultCostLoad) {
      return 'Error';
    }

    const responseValue = (
      resultCostResponse as CurrencyPercentageCostQueryResult | null
    )?.data?.getPortfolioCurrencyPercentageCost.amount;

    if (isNil(responseValue)) {
      return '0';
    }

    switch (portfolioManipulationAction) {
      case PortfolioPartitionAction.Starttrial:
      case PortfolioPartitionAction.Deposit:
        return getPrice(responseValue);
      case PortfolioPartitionAction.Withdraw: {
        const resultValue = getPrice(responseValue);
        return responseValue > 0 ? '≈ ' + resultValue : resultValue;
      }
      default:
        return '0';
    }
  }, [portfolioManipulationAction, errorOnResultCostLoad, resultCostResponse]);

  const selectMenuProps = useMemo<NonNullable<SelectProps['MenuProps']>>(
    () =>
      deepmerge(theme.components?.MuiSelect?.defaultProps?.MenuProps ?? {}, {
        slotProps: {
          paper: {
            sx: {
              minWidth: '230px !important',
            },
          },
        },
      }),
    [theme]
  );

  const onCurrencyChange = useCallback(
    (value: Maybe<string | string[]>) => {
      const ids = normalizeCurrencyIds(value);

      if (!multiple) {
        setSelectedCurrencies(getSelectedCurrencies(ids, options));
        return;
      }

      // Close the menu when the "Confirm" item is clicked. MUI Select toggles
      // CONFIRM_VALUE into the selection like any other item, so strip it out.
      if (ids.includes(CONFIRM_VALUE)) {
        const realIds = ids.filter(
          (id) => id !== CONFIRM_VALUE && id !== SELECT_ALL_VALUE
        );

        formikRef.current?.setFieldValue('currencyId', realIds);
        setSelectedCurrencies(getSelectedCurrencies(realIds, options));
        setSelectOpen(false);

        return;
      }

      // Handle "Select All" toggle from MUI Select's internal item click.
      // MUI's handleItemClick calls onChange after our custom onClick,
      // overriding whatever setFieldValue did — so we intercept here.
      if (ids.includes(SELECT_ALL_VALUE)) {
        const realIds = ids.filter((id) => id !== SELECT_ALL_VALUE);
        const allSelected = allOptionIds.every((id) => realIds.includes(id));
        const nextIds = allSelected ? [] : allOptionIds;

        formikRef.current?.setFieldValue('currencyId', nextIds);
        setSelectedCurrencies(getSelectedCurrencies(nextIds, options));

        return;
      }

      // Regular currency toggle — sync immediately to both formik and currencies state
      const realIds = ids.filter(
        (id) => id !== CONFIRM_VALUE && id !== SELECT_ALL_VALUE
      );

      formikRef.current?.setFieldValue('currencyId', realIds);
      setSelectedCurrencies(getSelectedCurrencies(realIds, options));

      return;
    },
    [allOptionIds, multiple, options, setSelectedCurrencies]
  );

  const handleCurrencyChange = useCallback(
    (value: InputValue) => {
      onCurrencyChange(getInputValue(value));
    },
    [onCurrencyChange]
  );

  const handleSubmit = useCallback<FormikConfig<FormFieldValues>['onSubmit']>(
    ({ currencyId, percent }, helpers) => {
      const currenciesIds = normalizeCurrencyIds(currencyId);
      return onSubmit(
        { currenciesIds, percent },
        // Helpers act on the same field shape; only the `values` key is renamed.
        helpers as unknown as FormikHelpers<FormValues>
      );
    },
    [onSubmit]
  );

  /*
   * Report calculation errors separately from rendering logic so the UI can show
   * a lightweight error state while Sentry still receives the actual exception.
   */
  useEffect(() => {
    if (errorOnResultCostLoad) {
      sendSentryError(errorOnResultCostLoad);
    }
  }, [errorOnResultCostLoad]);

  /*
   * Apply the default percentage rules after currency selection changes:
   * - multiple selected currencies are always processed as 100%;
   * - Start Trial and Deposit default to 100% for a single currency;
   * - Withdraw keeps the user-entered percentage for a single currency.
   */
  useEffect(() => {
    if (isEmpty(currencies)) return;

    const hasSeveralCurrencies = multiple && currencies.length > 1;
    const shouldForcePercentTo100 =
      hasSeveralCurrencies ||
      portfolioManipulationAction !== PortfolioPartitionAction.Withdraw;

    if (shouldForcePercentTo100) {
      forcePercentTo100();
    }
  }, [multiple, portfolioManipulationAction, currencies, forcePercentTo100]);

  /*
   * Cancel a pending debounced percentage update on unmount to avoid setting
   * state after the form has been removed.
   */
  useEffect(
    () => () => {
      debouncedSetPercentage.cancel();
    },
    [debouncedSetPercentage]
  );

  /*
   * In case of starting trial and picking
   * up currency which amount in euro
   * less than 2500 show notification*/
  useEffect(() => {
    const shouldShowAlert =
      currencies.length === 1 &&
      portfolioManipulationAction === PortfolioPartitionAction.Starttrial &&
      currencies[0].redLabel;

    if (!shouldShowAlert) {
      redLabelStartTrialAlertShownRef.current = false;
      return;
    }

    if (!redLabelStartTrialAlertShownRef.current) {
      setAlert({
        type: 'error',
        message: t(
          'PORTFOLIO_MANIPULATION_FORM__minCurrencyAmountStartTrialErrorText'
        ),
      });
      redLabelStartTrialAlertShownRef.current = true;
    }
  }, [t, portfolioManipulationAction, currencies, setAlert]);

  const disabled = disabledProp === true || isEmpty(options);

  return (
    <Formik<FormFieldValues>
      innerRef={formikRef}
      initialValues={{
        currencyId: multiple ? [] : '',
        percent: '',
      }}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      {({ dirty, isSubmitting, isValid, values }) => {
        const selectedCurrencyIds = normalizeCurrencyIds(values.currencyId);
        const isMultiSelection = multiple && selectedCurrencyIds.length > 1;

        return (
          <Form
            $formType={portfolioManipulationAction}
            $inputsAlwaysOneRowOnDesktop={inputsAlwaysOneRowOnDesktop}
            {...props}
          >
            {(() => {
              const selectInput = (
                <SelectInput
                  id={`${inputsPrefix}-form-currency-id`}
                  name={'currencyId'}
                  type={'number'}
                  select
                  disabled={disabled}
                  label={t('PORTFOLIO_MANIPULATION_FORM__currencyPlaceholder')}
                  fullWidth={false}
                  margin={'none'}
                  {...(multiple
                    ? {
                        slotProps: {
                          select: {
                            multiple: true,
                            open: selectOpen,
                            MenuProps: selectMenuProps,
                            onOpen: () => setSelectOpen(true),
                            onClose: handleSelectClose,
                            renderValue: (selected: unknown) => {
                              const selectedArray = Array.isArray(selected)
                                ? selected
                                : [];
                              if (selectedArray.length === 0) return '';
                              if (selectedArray.length > 1)
                                return t(
                                  'PORTFOLIO_MANIPULATION_FORM__currencyMultiplePlaceholder'
                                );
                              const option = options.find(
                                (o) => o.value === selectedArray[0]
                              );
                              return option?.label.toUpperCase() ?? '';
                            },
                          },
                        },
                      }
                    : {})}
                  onWheel={(e) =>
                    e.target instanceof HTMLElement && e.target.blur()
                  }
                  onChangeCb={handleCurrencyChange}
                >
                  {(() => {
                    const items = [];

                    if (withSelectAll && multiple) {
                      const isAllSelected = allOptionIds.every((id) =>
                        selectedCurrencyIds.includes(id)
                      );
                      items.push(
                        <DefMenuItem
                          key={'select-all'}
                          value={SELECT_ALL_VALUE}
                        >
                          <CurrencyCheckbox
                            checked={isAllSelected}
                            size="small"
                            disableRipple
                          />
                          <CurrencyLabel weight={'semiBold'} $red={false}>
                            {t(
                              isAllSelected
                                ? 'PORTFOLIO_MANIPULATION_FORM__selectAllDeselectText'
                                : 'PORTFOLIO_MANIPULATION_FORM__selectAllText'
                            )}
                          </CurrencyLabel>
                        </DefMenuItem>
                      );
                    }

                    items.push(
                      ...options.map(
                        ({
                          label,
                          value,
                          icon,
                          cost,
                          redLabel,
                          semiBoldLabel,
                        }) => {
                          const checked = selectedCurrencyIds.includes(value);
                          const red =
                            selectedCurrencyIds.length < 2 && !!redLabel;
                          const weight = semiBoldLabel ? 'semiBold' : undefined;

                          if (value === SELECT_ALL_VALUE) {
                            return null;
                          }

                          return (
                            <DefMenuItem key={value} value={value}>
                              {multiple && (
                                <CurrencyCheckbox
                                  checked={checked}
                                  size="small"
                                  disableRipple
                                />
                              )}
                              {!!icon && <SelectIconImg src={icon} />}
                              <CurrencyLabel weight={weight} $red={red}>
                                {label}
                              </CurrencyLabel>
                              {!isNil(cost) && (
                                <CurrencyCost $red={red}>
                                  {getPrice(cost)}
                                </CurrencyCost>
                              )}
                            </DefMenuItem>
                          );
                        }
                      )
                    );

                    if (multiple) {
                      items.push(
                        <ConfirmMenuItem
                          key={'confirm'}
                          value={CONFIRM_VALUE}
                          disableRipple
                        >
                          <ConfirmButton tabIndex={-1}>
                            {t('PORTFOLIO_MANIPULATION_FORM__confirmText')}
                          </ConfirmButton>
                        </ConfirmMenuItem>
                      );
                    }

                    return items;
                  })()}
                </SelectInput>
              );
              const percentInput = (
                <TextInput
                  id={`${inputsPrefix}-form-percent`}
                  name={'percent'}
                  type={'number'}
                  disabled={disabled || isMultiSelection}
                  label={'%'}
                  fullWidth={false}
                  slotProps={{
                    htmlInput: { min: 0, max: 100 },
                  }}
                  margin={'none'}
                  onWheel={(e) =>
                    e.target instanceof HTMLElement && e.target.blur()
                  }
                  onChangeCb={debouncedSetPercentage}
                />
              );
              if (
                portfolioManipulationAction ===
                PortfolioPartitionAction.Withdraw
              ) {
                return (
                  <>
                    {percentInput}
                    {selectInput}
                  </>
                );
              } else {
                return (
                  <>
                    {selectInput}
                    {percentInput}
                  </>
                );
              }
            })()}
            <ResultInput
              $inputsAlwaysOneRowOnDesktop={inputsAlwaysOneRowOnDesktop}
              disabled
              margin={'none'}
              slotProps={{
                input: {
                  startAdornment: loadingResultCost ? (
                    <InputAdornment position="start">
                      <Spinner size={20} />
                    </InputAdornment>
                  ) : null,
                },
              }}
              value={resultCostToRender}
            />
            <Button
              type={'submit'}
              loading={isSubmitting}
              disabled={disabled || !(dirty && isValid)}
              buttonStyles={css`
                height: 100%;
              `}
            >
              {t(buttonText)}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

/**
 * Internal Formik state. The currency select is bound directly to this field,
 * so it must mirror MUI Select's `value` shape: a string in single mode and an
 * array in `multiple` mode.
 */
type FormFieldValues = {
  currencyId: string | string[];
  percent: string;
};

/**
 * Public submit payload. Selected currencies are always normalized to an array
 * of ids so consumers don't have to branch on `string | string[]`.
 */
type FormValues = {
  currenciesIds: string[];
  percent: string;
};

type SelectedCurrency = {
  id: string;
  symbol: string;
  redLabel: boolean;
};

export type PortfolioManipulationFormCurrenciesOption = {
  label: string;
  value: string;
  icon: string | false;
  cost?: string | number;
  redLabel?: boolean;
  semiBoldLabel?: boolean;
};

const SELECT_ALL_VALUE = '__SELECT_ALL__';
const CONFIRM_VALUE = '__CONFIRM__';

const normalizeCurrencyIds = (
  currencyId: Maybe<string | string[]>
): string[] =>
  Array.isArray(currencyId) ? currencyId : currencyId ? [currencyId] : [];

const getSelectedCurrencies = (
  ids: string[],
  options: PortfolioManipulationFormCurrenciesOption[]
): SelectedCurrency[] =>
  ids.map((id) => {
    const currency = options.find(({ value }) => value === id);

    return {
      id,
      symbol: currency?.label || '',
      redLabel: !!currency?.redLabel,
    };
  });

const haveSameCurrencies = (
  currentCurrencies: SelectedCurrency[],
  selectedCurrencies: SelectedCurrency[]
): boolean => {
  if (currentCurrencies.length !== selectedCurrencies.length) {
    return false;
  }

  const selectedCurrenciesById = new Map(
    selectedCurrencies.map((currency) => [currency.id, currency])
  );

  return currentCurrencies.every((currency) => {
    const selectedCurrency = selectedCurrenciesById.get(currency.id);

    return (
      !!selectedCurrency &&
      currency.symbol === selectedCurrency.symbol &&
      currency.redLabel === selectedCurrency.redLabel
    );
  });
};

const resultBreakpoints = {
  tablet: 500,
  desktop: 1500,
} as const;

const Form = styled(DefForm)<{
  $formType: PortfolioPartitionAction;
  $inputsAlwaysOneRowOnDesktop: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $formType }) =>
    $formType === PortfolioPartitionAction.Withdraw
      ? '2.2fr 3.8fr 3.4fr'
      : '3.8fr 2.2fr 3.4fr'};
  grid-gap: 15px;

  ${getUpMedia(resultBreakpoints.tablet)} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  ${({ $inputsAlwaysOneRowOnDesktop, $formType }) =>
    !$inputsAlwaysOneRowOnDesktop &&
    css`
      ${getUpMedia('lg')} {
        grid-template-columns: repeat(2, 1fr);
      }

      ${getUpMedia(resultBreakpoints.desktop)} {
        grid-template-columns: ${$formType === PortfolioPartitionAction.Withdraw
          ? '2.2fr 3.8fr 3fr'
          : '3.8fr 2.2fr 3fr'};
      }
    `};

  ${getUpMedia(1600)} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// noinspection CssUnusedSymbol
const FormikInput = styled(DefFormikInput)`
  .MuiInputLabel-root.Mui-disabled {
    display: none;
  }
`;

const TextInput = styled(FormikInput)`
  input[type='number']::-webkit-inner-spin-button {
    opacity: 1;
  }
`;

const CurrencyListItemText = styled(Text)<{ $red: boolean }>`
  ${({ $red }) =>
    $red &&
    css`
      color: ${getColor('lightRed')};
    `}
`;

const CurrencyLabel = styled(CurrencyListItemText)`
  margin-right: 10px;
`;

const CurrencyCost = styled(CurrencyListItemText)`
  margin-left: auto;
`;

const CurrencyCheckbox = styled(Checkbox)`
  margin-right: 6px;
`;

// Confirmation item at the end of the currency list.
// noinspection CssUnusedSymbol
const ConfirmMenuItem = styled(DefMenuItem)`
  && {
    padding: 18px 0 12px;
    background-color: ${getColor('white')};
  }

  &&,
  &&:hover,
  &&.Mui-focusVisible,
  &&.Mui-selected,
  &&.Mui-selected:hover {
    background-color: ${getColor('white')};
  }
`;

// Visual only — the enclosing menu item handles the click via CONFIRM_VALUE,
// so the button itself must not capture pointer events or keyboard focus.
const ConfirmButton = styled(DefMuiButton)`
  //pointer-events: none;
`;

const SelectInput = styled(FormikInput)`
  /*noinspection CssUnusedSymbol*/
  .MuiSelect-select {
    display: flex;
    align-items: center;

    ${CurrencyCost} {
      display: none;
    }
  }
`;

const SelectIconImg = styled.img`
  width: 22px;
  height: 22px;
  margin-right: 10px;
  object-fit: contain;

  ${({ theme }) => theme.breakpoints.down('xs')} {
    width: 18px;
    height: 18px;
    margin-right: 7px;
  }
`;

const ResultInput = styled(Input)<{
  $inputsAlwaysOneRowOnDesktop: boolean;
}>`
  ${({ $inputsAlwaysOneRowOnDesktop }) =>
    !$inputsAlwaysOneRowOnDesktop &&
    css`
      ${getUpMedia('lg')} {
        grid-column: 1 / -1;
      }
    `};

  ${getUpMedia(resultBreakpoints.desktop)} {
    grid-column: auto;
  }

  /*noinspection CssUnusedSymbol*/
  .MuiInputBase-input {
    text-align: right;
  }
`;

const Button = styled(DefButton)`
  width: 100%;
  grid-column: 1 / -1;
` as typeof DefButton;

type InputValue = Parameters<NonNullable<FormikInputProps['onChangeCb']>>[0];
const getInputValue = (value: InputValue): string | string[] => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (!value || typeof value !== 'object' || !('target' in value)) {
    return '';
  }

  const target = value.target;

  if (!target || typeof target !== 'object' || !('value' in target)) {
    return '';
  }

  const targetValue = target.value;

  if (Array.isArray(targetValue)) {
    return targetValue.map(String);
  }

  return isNil(targetValue) ? '' : String(targetValue);
};

const getSchema = (
  t: TFunction,
  multiple: boolean
): Yup.ObjectSchema<FormFieldValues> =>
  // @ts-ignore
  Yup.object().shape({
    currencyId: multiple
      ? Yup.array()
          .of(Yup.string())
          .min(1, t('COMMON_FORMS_ERROR__required'))
          .required(t('COMMON_FORMS_ERROR__required'))
      : Yup.string().required(t('COMMON_FORMS_ERROR__required')),
    percent: Yup.string()
      .test({
        name: 'min-value-test',
        test: (value) => {
          if (!value) {
            return true;
          }

          const numericValue = Number(value);

          return Number.isFinite(numericValue) && numericValue >= 0;
        },
        message: t('PORTFOLIO_MANIPULATION_FORM__minValueError'),
      })
      .test({
        name: 'max-value-test',
        test: (value) => {
          if (!value) {
            return true;
          }

          const numericValue = Number(value);

          return Number.isFinite(numericValue) && numericValue <= 100;
        },
        message: t('PORTFOLIO_MANIPULATION_FORM__maxValueError'),
      })
      .required(t('COMMON_FORMS_ERROR__required')),
  });

export { PortfolioManipulationForm };
